import dayjs from "dayjs";
import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Button, CheckPicker, DateRangePicker, FormGroup, List, Notification, SelectPicker, Loader, Input } from "rsuite";
import { ValueType } from "rsuite/lib/DateRangePicker";
import { ParametersGlobals } from "../../../../core/parametersGlobals/ParametersGlobals";
import axios from "../../../../utilities/axios";
import styles from "./styles/FinancialChartPerStore.module.scss";

interface FinancialChartPerStoreProps {
    storesData: { label: string; value: number }[];
}

export default function FinancialChartPerStore({ storesData }: FinancialChartPerStoreProps) {
    //Filtros
    const [selectedStore, setSelectedStore] = useState<number | null>(null);
    const [selectedErrors, setSelectedErrors] = useState<number[]>([]);
    const [selectedPeriod, setSelectedPeriod] = useState<ValueType | undefined>([
        dayjs().startOf('month').toDate(),
        dayjs().endOf('month').toDate(),
    ]);

    //Utilidades
    const [loading, setLoading] = useState<boolean>(false);
    const [formattedErrorsData, setFormattedErrorsData] = useState<{ value: number, label: string }[]>([]);
    const [chartState, setChartState] = useState<any>(null);
    const [detailsChartState, setDetailsChartState] = useState<any>(null);
    const [detailErrorSelected, setDetailErrorSelected] = useState<number | null>(null);
    // const [detailsData, setDetailsData] = useState<any[]>([]);
    // const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    useEffect(() => {
        try {
            setLoading(true);

            fetchErrorsPerGroup();
        } catch (error) {
            console.log("Erro useEffect financialChartPerStore ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        } finally {
            setLoading(false);
        }
    }, [])

    const fetchErrorsPerGroup = async () => {
        try {
            const response = await axios.get(`/api/v1/dashboard/chart/financial/custom/select-error-data`, {
                params: {
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID
                }
            });

            const filteredErrors = response.data
                .filter((error: any) => error.error_active === true)
                .map((error: any) => ({
                    value: error.error_id,
                    label: error.error_name
                }));

            const allErrorIds = filteredErrors.map((e: any) => e.value);

            setFormattedErrorsData(filteredErrors);
            setSelectedErrors(allErrorIds); // Marca todos os Lançamentos como selecionados
        } catch (error) {
            console.log("Erro financialChartPerStore.fetchErrorsPerGroup ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        }
    };

    const clearFilters = () => {
        const allErrorIds = formattedErrorsData.map((e) => e.value);

        setSelectedErrors(allErrorIds); // Restaurar todos como marcados
        setSelectedStore(null);
        setSelectedPeriod([
            dayjs().startOf('month').toDate(),
            dayjs().endOf('month').toDate(),
        ]);
        setChartState(null);
        setDetailErrorSelected(null);
        setDetailsChartState(null);
    };

    const fetchChartData = async () => {
        if (!selectedPeriod || !selectedStore) {
            Notification.warning({ title: 'Aviso', description: 'Selecione um período e uma loja para visualizar os dados', placement: 'topEnd', close: true });
            return;
        }

        try {
            setLoading(true);
            setDetailErrorSelected(null)
            setDetailsChartState(null)

            const { data } = await axios.get(`/api/v1/dashboard/financial/secondary/chart/store`, {
                params: {
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID,
                    store: selectedStore,
                    errors: selectedErrors,
                    period: selectedPeriod
                }
            });

            const updatedChartOptions = {
                ...data.options,
                chart: {
                    ...(data.options.chart || {}),
                    zoom: {
                        enabled: false,
                    },
                    events: {
                        dataPointSelection: function (event: any, chartContext: any, config: any) {
                            const index = config.dataPointIndex;
                            const clickedError = data.customData?.[index];

                            if (clickedError) {
                                handleSearchDetails(clickedError);
                            }
                        }
                    }
                }
            };

            setChartState({
                ...data,
                options: updatedChartOptions
            });
        } catch (error) {
            console.log("Erro financialChartPerStore.fetchChartData ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar dados', placement: 'topEnd', close: true });
        } finally {
            setLoading(false);
        }
    }

    const handleSearchDetails = async (clickedError: any) => {
        try {
            setLoading(true);
            setDetailErrorSelected(clickedError.errorName);

            const { data } = await axios.get(`/api/v1/dashboard/financial/secondary/chart/store/details`, {
                params: {
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID,
                    store: selectedStore,
                    error: clickedError.errorId,
                    period: selectedPeriod
                }
            });

            // Formatar os labels com base no detailsList
            const formattedLabels = data.detailsChart.options.labels.map((label: string) => {
                const matched = data.detailsList.find((item: any) => item.userName === label);
                const count = matched?.count ?? 0;
                return `${label}: ${count}`;
            });

            // Aplicar os labels formatados nas opções do gráfico
            data.detailsChart.options.labels = formattedLabels;

            // Atualizar tooltip também se necessário
            data.detailsChart.options.tooltip = {
                y: {
                    formatter: function () {
                        return ``;
                    },
                    title: {
                        formatter: (seriesName: any) => seriesName,
                    },
                }
            };

            // Atualizar estado
            setDetailsChartState(data.detailsChart);

        } catch (error) {
            console.log("Erro financialChartPerStore.handleSearchDetails ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar dados', placement: 'topEnd', close: true });
        } finally {
            setLoading(false);
        }
    }

    // const toggle = (userName: string) => {
    //     setExpandedKeys(prev =>
    //         prev.includes(userName)
    //             ? prev.filter(k => k !== userName)
    //             : [...prev, userName]
    //     );
    // };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.containerFilters}>
                    <div>
                        <FormGroup>
                            <label>Período</label>
                            <DateRangePicker
                                placeholder="Selecione uma data"
                                format="DD/MM/YYYY"
                                ranges={[]}
                                value={selectedPeriod}
                                onChange={(value) => setSelectedPeriod(value)}
                                className={styles.customDateRangePicker}
                            />
                        </FormGroup>

                        <FormGroup className={styles.customFilterFormGroup}>
                            <label>Loja</label>
                            <SelectPicker
                                data={storesData}
                                placeholder="Selecione uma loja..."
                                value={selectedStore}
                                onChange={(value) => setSelectedStore(value)}
                                className={styles.customSelectPicker}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Grupo de Lançamentos</label>
                            <Input
                                value="FINANCEIRO"
                                style={{ width: 150 }}
                                disabled
                            />
                            <span className={styles.informationText}>
                                * Obrigatório
                            </span>
                        </FormGroup>

                        <FormGroup className={styles.customFilterFormGroup}>
                            <label>Lançamentos</label>
                            <CheckPicker
                                data={formattedErrorsData}
                                placeholder="Selecione os Lançamentos..."
                                cleanable
                                searchable
                                value={selectedErrors}
                                onChange={(value) => setSelectedErrors(value)}
                                className={styles.customSelectPicker}
                            />
                        </FormGroup>
                    </div>

                    <div>
                        <Button
                            color="cyan"
                            appearance="primary"
                            onClick={clearFilters}
                            loading={loading}
                            className={styles.customButton}
                        >
                            Limpar
                        </Button>

                        <Button
                            appearance="primary"
                            onClick={fetchChartData}
                            loading={loading}
                            className={styles.customButton}
                        >
                            Filtrar
                        </Button>
                    </div>
                </div>

                <div className={styles.containerCharts}>
                    {chartState ? (
                        <>
                            <div className={styles.secundaryChartCard}>
                                <h5 className={styles.chartTitle}>Por Loja</h5>

                                <ReactApexChart options={chartState?.options} series={chartState?.series} type="pie" height={400} />
                            </div>

                            <div className={styles.secundaryChartCard}>
                                <h5 className={styles.chartTitle}>Detalhes {detailErrorSelected ? ` - ${detailErrorSelected}` : ""}</h5>

                                {loading ? (
                                    <div className={styles.noDataContainer} style={{ height: '90%', marginTop: '1rem' }}>
                                        <Loader size="md" content="Carregando detalhes..." />
                                    </div>
                                ) : (
                                    detailsChartState ? (
                                        <ReactApexChart options={detailsChartState?.options} series={detailsChartState?.series} type="pie" height={400} />
                                    ) : (
                                        <div className={styles.noDataContainer} style={{ height: '90%', marginTop: '1rem' }}>
                                            <p className={styles.noDataText}>Selecione um erro para visualizar os detalhes...</p>
                                        </div>
                                    )
                                )}

                                {/* {loading ? (
                                    <div className={styles.noDataContainer} style={{ height: '90%', marginTop: '1rem' }}>
                                        <Loader size="md" content="Carregando detalhes..." />
                                    </div>
                                ) : detailsData.length > 0 ? (
                                    <div className={styles.employeeListWrapper}>
                                        <List bordered hover>
                                            {detailsData.map((group: any) => (
                                                <>
                                                    <List.Item
                                                        key={group.userName}
                                                        className={styles.groupItem}
                                                        onClick={() => toggle(group.userName)}
                                                    >
                                                        <div className={styles.groupHeader}>
                                                            <span>{group.userName}</span>
                                                            <span>
                                                                {group.count} ocorrência{group.count > 1 ? 's' : ''} —{' '}
                                                                {group.totalValue.toLocaleString('pt-BR', {
                                                                    style: 'currency',
                                                                    currency: 'BRL',
                                                                    minimumFractionDigits: 2
                                                                })}
                                                            </span>
                                                        </div>
                                                    </List.Item>

                                                    {expandedKeys.includes(group.userName) &&
                                                        group.occurrences.map((item: any, i: number) => (
                                                            <List.Item key={`${group.userName}-${i}`} className={styles.subItem}>
                                                                <div><b>Data:</b> {dayjs(item.date).format('DD/MM/YYYY')}</div>
                                                                <div><b>Erro:</b> {item.error}</div>
                                                                <div><b>Valor:</b> R$ {item.value.toLocaleString('pt-BR', {
                                                                    minimumFractionDigits: 2
                                                                })}</div>
                                                            </List.Item>
                                                        ))}
                                                </>
                                            ))}
                                        </List>
                                    </div>
                                ) : (
                                    <div className={styles.noDataContainer} style={{ height: '90%', marginTop: '1rem' }}>
                                        <p className={styles.noDataText}>Selecione um erro para visualizar os detalhes...</p>
                                    </div>
                                )} */}
                            </div>
                        </>
                    ) : (
                        <div className={styles.noDataContainer}>
                            <p className={styles.noDataText}>Nenhum dado encontrado</p>
                        </div>
                    )}
                </div>
            </div >
        </>
    )
}
